import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchBenefitPlanDetail, fetchBenefitPlans, fetchBenefitPlansByAccountKey, fetchBenefitProvision, fetchBenefitProvisionFact, fetchBenefits, fetchCoverageTierSetHistoryFact, fetchProvisionDetailsByBenefitPlan, fetchTemplateRiderComp, fetchValueFromValueList } from "../../utils/apiUtils";
import { useEffect, useState, useCallback } from "react";
import { useInView } from 'react-intersection-observer'
import SharedTable from "./SharedTable";
import { loadAccountHccIdentificationNumber, loadBenefitPlans, loadTemplates } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import { AsyncPaginate } from "react-select-async-paginate";
import { useLocation, useNavigate } from "react-router";
import { useMultiStepFormStore } from "../../store/multiStepFormStore";
import accumMapping from './../../mapping/accum-mapping.json'
import debounce from 'lodash.debounce';
import { queryClient } from "../../App";
import { exportToCsv } from "../../utils/downloadUtils";

function getWordsInDoubleQuotesAfterIs(text) {
    // The regular expression:
    // is          - matches the literal string "is"
    // \\s* - matches zero or more whitespace characters
    // "           - matches the opening double quote
    // ([^"]*)     - This is the capturing group:
    //               [^"]  - matches any character that is NOT a double quote
    //               * - matches the preceding character zero or more times
    // "           - matches the closing double quote
    const regex = /\sis\s*"([^"]*)"/g;
    const matches = text.match(regex);
    const result = [];

    if (matches) {
        matches.forEach(match => {
            // For each full match (e.g., 'is "OON Office Visit PCP Coins"'),
            // we need to extract only the content within the double quotes.
            // We can do this by using another regex or by string manipulation.
            // Using another regex to capture the group:
            const innerMatch = /"([^"]*)"/.exec(match);
            if (innerMatch && innerMatch[1]) {
                result.push(innerMatch[1]);
            }
        });
    }
    return result;
}

const limit = 10
//'benefitProvisionKey', 'benefitPlanHistoryFactKey', 'productKey', 'benefitPlanKey',
let columns = ['provisionTypeName', 'provision', 'provisionWithValues', 'provisionWithNamesAndVals', 'valueListVariable'];

const BenefitPlanProvision = ({ benefitPlanName, benefitPlanHccId, benefitPlanKey, benefitPlanHistoryFactKey }) => {

    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadingError, setDownloadingError] = useState('')
    const [searchValue, setSearchValue] = useState('');
    const [decouncedSearchValue, setDecouncedSearchValue] = useState('')
    const [selectedBenefitPlan, setSelectedBenefitPlan] = useState(null)

    const { ref, inView } = useInView()

    useEffect(() => {
        setSelectedBenefitPlan(benefitPlanName ? {
            label: benefitPlanName, value: benefitPlanHccId,
            additional: { benefitPlanName, benefitPlanHccId, benefitPlanKey, benefitPlanHistoryFactKey }
        } : null)
    }, [])

    const queryKey = ['provision', limit, selectedBenefitPlan, decouncedSearchValue];
    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage,
        status,
        refetch,

    } = useInfiniteQuery({
        queryKey,
        queryFn: async ({ pageParam }) => {
            let params = pageParam || {}


            if (decouncedSearchValue) {
                params.provisionSearchText = decouncedSearchValue
            }

            const results = await fetchProvisionDetailsByBenefitPlan({
                ...params,
                benefitPlanKey: selectedBenefitPlan.additional?.benefitPlanKey,
                benefitPlanHistoryFactKey: selectedBenefitPlan.additional?.benefitPlanHistoryFactKey,
            })

            return {
                ...results, rows: results.rows.map(r => ({
                    ...r,
                    valueListVariable: getWordsInDoubleQuotesAfterIs(r.provision)?.join(' & ')
                }))
            }
        },

        select: (data) => {
            //   console.log({ data });
            return { count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows).flatMap((x) => x) };
        },
        initialPageParam: 0,
        placeholderData: (previousData, previousQuery) => previousData,
        getNextPageParam: (lastPage, pages) => {
            const hasMore = lastPage.rows?.length === limit

            if (!hasMore) return undefined
            return {
                offset: pages.reduce((accum, page) => {
                    return accum + page.rows.length;
                }, 0),
            };
        },
        staleTime: 0,
        enabled: !!selectedBenefitPlan,
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage()
        }
    }, [inView])

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    const downloadAllValueList = async () => {
        setIsDownloading(true)
        setDownloadingError(null)
        try {
            let hasMore = false;
            const items = []
            let params = { offset: 0 }
            if (decouncedSearchValue) {
                params.provisionSearchText = decouncedSearchValue
            }

            do {
                const results = await fetchProvisionDetailsByBenefitPlan({
                    ...params,
                    benefitPlanKey: selectedBenefitPlan.additional?.benefitPlanKey,
                    benefitPlanHistoryFactKey: selectedBenefitPlan.additional?.benefitPlanHistoryFactKey,
                })

                results.rows.forEach(r => items.push({
                    // ...r,
                    provisionTypeName: r.provisionTypeName,
                    provision: r.provision,
                    valueListVariable: getWordsInDoubleQuotesAfterIs(r.provision)?.join(' & ')
                }))

                hasMore = results.rows?.length === limit
                params.offset += results.rows?.length

            } while (hasMore)

            exportToCsv({
                fileNamePrefix: decouncedSearchValue ? `${selectedBenefitPlan?.value}-${decouncedSearchValue}` : `${selectedBenefitPlan?.value}`,
                items, //state.items,
                headers: ['provisionTypeName', 'provision', 'valueListVariable'],
            });
        } catch (err) {
            setDownloadingError(err.message)
        } finally {
            setIsDownloading(false)
        }


    }

    return (
        <div className={'container '} style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                    <p style={{ fontSize: '1.2rem', fontWeight: '600' }}>Provision  ( {data?.count || 0} )</p>
                    <p className="sub-title">Benefit Plan Provision</p>
                </div>

                {isDownloading ? <div className="loader" /> : selectedBenefitPlan && <div >
                    <p className="clickable-link" style={{ color: 'var(--green)', padding: '1rem' }} onClick={downloadAllValueList}>Download</p>
                </div>}

                {downloadingError && <p className="danger">{downloadingError}</p>}

            </div>


            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', paddingRight: '1rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', justifyContent: 'start', width: '100%', padding: '1rem 0rem' }}>

                    {!benefitPlanKey && <AsyncPaginate
                        placeholder={"Select a benefit plan"}
                        className="react-select-container large-input"
                        classNamePrefix={"react-select"}
                        debounceTimeout={300}
                        value={selectedBenefitPlan}
                        loadOptions={loadBenefitPlans}
                        onChange={setSelectedBenefitPlan}
                        styles={customStyles}
                        isClearable
                    />}


                    <input
                        style={{ fontSize: '1rem', minWidth: '250px', padding: '1rem', borderRadius: '.4rem', border: '1px solid var(--lighterGrey)', outline: 'none' }}
                        type="text"
                        id={'search'}
                        placeholder="search..."
                        // list="items"
                        autoComplete="off"
                        // onFocus={() => setCurrentPage(0)}
                        onChange={(e) => {

                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />

                </div>


            </div>


            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        queryClient.resetQueries({ queryKey });

                        setSelectedBenefitPlan(null)
                        setDecouncedSearchValue("");
                        setSearchValue("");

                    }}
                >
                    Clear filters
                </p>


            </div>


            {selectedBenefitPlan ? status === 'loading' ? (
                <p>Loading...</p>
            ) : status === 'error' ? (
                <span>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={'provision'}
                        columns={columns}
                        items={data?.rows}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        ref={ref}
                    />

                </>) : <></>}

        </div >
    )
}

export default BenefitPlanProvision